import { Button } from "@/components/primitives/button";
import { CirclePlus } from "lucide-react";
import { useRef, useEffect } from "react";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";

import {
  ALL_ALLOWED_TYPES,
  ALLOWED_DOC_TYPES,
  ALLOWED_MEDIA_TYPES,
} from "@/config/constants";

/**
 * Purpose: Mobile-specific attachment menu component
 *
 * How it works:
 * 1. Provides a single file input that accepts all allowed file types
 * 2. Validates file types and routes to appropriate handler
 * 3. Resets input when attachments are processed
 *
 * Key features:
 * - Single input for all file types
 * - File type validation with error feedback
 * - Input reset when attachments are processed
 */
export default function MobileAttachmentMenu() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleMediaAttachment, newMediaAttachment } =
    useMediaMessageSenderState();

  const { handleFileAttachment, newFileAttachment } =
    useFileMessageSenderState();

  const mobileInputRef = useRef<HTMLInputElement>(null);

  // Reset mobile input when either dialog closes
  useEffect(() => {
    if (!newFileAttachment && !newMediaAttachment && mobileInputRef.current) {
      mobileInputRef.current.value = "";
    }
  }, [newFileAttachment, newMediaAttachment]);

  const handleMobileInputClick = () => {
    mobileInputRef.current?.click();
  };

  const handleMobileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const isMedia = ALLOWED_MEDIA_TYPES.includes(file.type);
    const isPdf = ALLOWED_DOC_TYPES.includes(file.type);

    if (isMedia) {
      handleMediaAttachmentInputChange(e);
    } else if (isPdf) {
      handleFileAttachmentInputChange(e);
    } else {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
    }
  };

  const handleMediaAttachmentInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const isMedia = ALLOWED_MEDIA_TYPES.includes(file.type);
    if (!isMedia) {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
      return;
    }
    handleMediaAttachment(file);
  };

  const handleFileAttachmentInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const isPdf = ALLOWED_DOC_TYPES.includes(file.type);
    if (!isPdf) {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
      return;
    }
    handleFileAttachment(file);
  };

  return (
    <>
      <input
        type="file"
        name="attachments"
        accept={ALL_ALLOWED_TYPES.join(", ")}
        ref={mobileInputRef}
        className="hidden"
        onChange={handleMobileInputChange}
      />
      <Button
        asChild
        className="size-5 lg:cursor-pointer"
        size="icon"
        variant="ghost"
        onClick={handleMobileInputClick}
      >
        <CirclePlus />
      </Button>
    </>
  );
}
